import { useEffect } from 'react';
import { useController, useForm } from 'react-hook-form';
import { Alert, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { ContainedButton, Dialog, OutlinedButton, TextInputField } from '@shared/components';
import { CustomerAssortmentOption } from '../CustomerAssortmentSelect';
import { createCustomerSchema, CreateCustomerSchema } from './createCustomerSchema';
import { CustomerTemplateSelect } from './CustomerTemplateSelect';
import { yupResolver } from '@hookform/resolvers/yup';

interface Props {
  open: boolean;
  customerAssortments: CustomerAssortmentOption[];
  isSubmitting: boolean;
  nameError?: string;
  submitError?: string;
  onSubmit: (values: CreateCustomerSchema) => void;
  onClose: () => void;
}

export function CreateCustomerDialog({
  open,
  customerAssortments,
  isSubmitting,
  nameError,
  submitError,
  onSubmit,
  onClose,
}: Props) {
  const t = useAppTFunction();

  const { control, handleSubmit, setError, reset, formState } = useForm<CreateCustomerSchema>({
    resolver: yupResolver(createCustomerSchema(t)),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      discountMatrixTemplateAssortmentId: null,
      assortmentTemplateAssortmentId: null,
    },
  });
  const nameController = useController({ name: 'name', control });
  const discountMatrixTemplateController = useController({
    name: 'discountMatrixTemplateAssortmentId',
    control,
  });
  const assortmentTemplateController = useController({
    name: 'assortmentTemplateAssortmentId',
    control,
  });

  useEffect(() => {
    if (nameError) setError('name', { type: 'server', message: nameError });
  }, [nameError, setError]);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('app.createCustomer.title')}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {submitError && <Alert severity="error">{submitError}</Alert>}
          <TextInputField
            {...nameController.field}
            label={t('app.createCustomer.nameLabel')}
            error={nameController.fieldState.error?.message}
            disabled={isSubmitting}
            required
            fullWidth
            autoFocus
            margin="none"
          />
          {customerAssortments.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('app.createCustomer.template.description')}
              </Typography>
              <CustomerTemplateSelect
                label={t('app.createCustomer.template.discountMatrixLabel')}
                customerAssortments={customerAssortments}
                value={discountMatrixTemplateController.field.value}
                onChange={discountMatrixTemplateController.field.onChange}
                disabled={isSubmitting}
              />
              <CustomerTemplateSelect
                label={t('app.createCustomer.template.assortmentLabel')}
                customerAssortments={customerAssortments}
                value={assortmentTemplateController.field.value}
                onChange={assortmentTemplateController.field.onChange}
                disabled={isSubmitting}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <OutlinedButton color="secondary" size="small" onClick={onClose} disabled={isSubmitting}>
            {t('app.createCustomer.cancel')}
          </OutlinedButton>
          <ContainedButton
            type="submit"
            color="secondary"
            size="small"
            loading={isSubmitting}
            disabled={!formState.isValid}
          >
            {t('app.createCustomer.submit')}
          </ContainedButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
